/**
 * DateInput Component — DD/MM/YYYY 格式日期輸入器
 * 設計說明：frontend/src/components/DateInput.tsx
 *
 * 文字框顯示固定 DD/MM/YYYY 格式；📅 按鈕觸發隱藏原生日曆。
 * value / onChange 均使用 DD/MM/YYYY 字串。
 */
import React, { useRef } from 'react';

export interface DateInputProps {
  value: string;                   // DD/MM/YYYY
  onChange: (v: string) => void;   // 回傳 DD/MM/YYYY
  disabled?: boolean;
}

function toPickerDate(ddmmyyyy: string): string {
  if (!ddmmyyyy) return '';
  const parts = ddmmyyyy.split('/');
  if (parts.length !== 3) return '';
  const [dd, mm, yyyy] = parts;
  if (!dd || !mm || !yyyy || yyyy.length !== 4) return '';
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}

function fromPickerDate(yyyymmdd: string): string {
  if (!yyyymmdd) return '';
  const parts = yyyymmdd.split('-');
  if (parts.length !== 3) return '';
  const [yyyy, mm, dd] = parts;
  return `${dd}/${mm}/${yyyy}`;
}

const DateInput: React.FC<DateInputProps> = ({ value, onChange, disabled }) => {
  const pickerRef = useRef<HTMLInputElement>(null);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value.replace(/[^0-9/]/g, ''));
  };

  const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) onChange(fromPickerDate(e.target.value));
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={handleTextChange}
        disabled={disabled}
        placeholder="DD/MM/YYYY"
        maxLength={10}
        style={{ width: 130, padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, letterSpacing: 1 }}
      />
      {!disabled && (
        <>
          <button
            type="button"
            onClick={() => pickerRef.current?.showPicker?.()}
            style={{ padding: '6px 8px', border: '1px solid #d0d0d0', borderRadius: 6, background: '#fafafa', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
            title="開啟日曆">
            📅
          </button>
          <input
            ref={pickerRef}
            type="date"
            value={toPickerDate(value)}
            onChange={handlePickerChange}
            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
            tabIndex={-1}
          />
        </>
      )}
    </div>
  );
};

export default DateInput;
